import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Bell, CheckCheck, Trash2, Heart, MessageCircle, ShoppingCart, Package, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const ICONS = {
  like: Heart,
  comment: MessageCircle,
  sale: ShoppingCart,
  purchase: Package,
  payout: Sparkles,
};

export default function NotificationsPage() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadNotifications(); }, []);

  const loadNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data.notifications || []);
      setUnread(res.data.unread_count || 0);
    } catch { toast.error('Failed to load notifications'); }
    finally { setLoading(false); }
  };

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnread(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await api.post('/notifications/read-all');
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnread(0);
    toast.success('All marked as read');
  };

  const clearAll = async () => {
    await api.delete('/notifications');
    setNotifications([]);
    setUnread(0);
    toast.success('Notifications cleared');
  };

  const deleteOne = async (id) => {
    await api.delete(`/notifications/${id}`);
    const wasUnread = notifications.find(n => n.id === id && !n.read);
    setNotifications(prev => prev.filter(n => n.id !== id));
    if (wasUnread) setUnread(prev => Math.max(0, prev - 1));
  };

  const timeAgo = (date) => {
    const s = Math.floor((Date.now() - new Date(date)) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };

  if (loading) return <div className="text-center py-20 text-muted-foreground text-sm">Loading...</div>;

  return (
    <div className="max-w-2xl mx-auto animate-in" data-testid="notifications-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-2xl md:text-3xl">Notifications</h1>
          {unread > 0 && <p className="text-sm text-gold mt-1">{unread} unread</p>}
        </div>
        <div className="flex gap-2">
          {unread > 0 && (
            <Button data-testid="mark-all-read-btn" variant="outline" size="sm" className="rounded-sm text-xs" onClick={markAllRead}>
              <CheckCheck size={12} className="mr-1" /> Mark All Read
            </Button>
          )}
          {notifications.length > 0 && (
            <Button data-testid="clear-all-btn" variant="outline" size="sm" className="rounded-sm text-xs text-muted-foreground" onClick={clearAll}>
              <Trash2 size={12} className="mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>

      {notifications.length === 0 ? (
        <div className="border border-border/40 p-12 text-center" data-testid="no-notifications">
          <Bell size={32} className="mx-auto text-muted-foreground/20 mb-4" />
          <p className="text-sm text-muted-foreground">No notifications yet</p>
          <p className="text-xs text-muted-foreground/60 mt-1">You'll see likes, comments, and sales here</p>
        </div>
      ) : (
        <div className="border border-border/40 divide-y divide-border/20">
          {notifications.map((n, i) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <div
                key={n.id}
                className={`flex items-start gap-3 p-4 transition-colors cursor-pointer group ${!n.read ? 'bg-gold/5' : 'hover:bg-mist/30'}`}
                onClick={() => !n.read && markRead(n.id)}
                data-testid={`notification-${n.id}`}
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${!n.read ? 'bg-gold/15' : 'bg-mist'}`}>
                  <Icon size={14} className={!n.read ? 'text-gold' : 'text-muted-foreground/50'} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm ${!n.read ? 'font-medium' : ''}`}>{n.title}</p>
                    {!n.read && <div className="w-2 h-2 rounded-full bg-gold shrink-0" />}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{n.message}</p>
                  <p className="text-[10px] text-muted-foreground/50 mt-1">{timeAgo(n.created_at)}</p>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {n.link && (
                    <Link to={n.link} className="text-[10px] text-gold hover:underline" onClick={e => e.stopPropagation()}>
                      View
                    </Link>
                  )}
                  <button onClick={e => { e.stopPropagation(); deleteOne(n.id); }} className="p-1 text-muted-foreground/30 hover:text-destructive" data-testid={`delete-notif-${n.id}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
